import {
  AwaitContainer,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Datepicker,
  Table,
  TableColumn,
} from '@ibsys/kit';
import { FC, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BitrixCallListItem, BitrixCallListParams, BitrixDateRange, useBitrixCalls } from '@/entities/integrations';

const PER_PAGE = 20;

// Форма содержит два независимых интервала, оба обязательны для отправки запроса
type BitrixFiltersForm = {
  startDate?: BitrixDateRange;
  endDate?: BitrixDateRange;
};

const hasAnyDate = (range?: BitrixDateRange): boolean => Boolean(range?.from || range?.to);

export const BitrixCallsSection: FC = () => {
  const { t } = useTranslation(['integrations', 'common']);

  // Активные фильтры — то, что реально отправлено в запрос
  const [activeParams, setActiveParams] = useState<BitrixCallListParams | undefined>(undefined);
  const [page, setPage] = useState(1);

  const { control, handleSubmit, reset, watch } = useForm<BitrixFiltersForm>({
    defaultValues: { startDate: undefined, endDate: undefined },
  });

  // Запросы делает только хук — кнопка просто переключает activeParams
  const { data, isLoading, isFetching } = useBitrixCalls(
    activeParams
      ? {
          ...activeParams,
          limit: PER_PAGE,
          offset: (page - 1) * PER_PAGE,
        }
      : undefined,
  );

  const handleSearch = useCallback((values: BitrixFiltersForm) => {
    // Сбрасываем страницу при новом поиске, чтобы не «зависнуть» на последней
    setPage(1);
    setActiveParams({
      dateFrom: hasAnyDate(values.startDate) ? values.startDate : undefined,
      dateTo: hasAnyDate(values.endDate) ? values.endDate : undefined,
    });
  }, []);

  const handleClear = useCallback(() => {
    reset({ startDate: undefined, endDate: undefined });
    setActiveParams(undefined);
    setPage(1);
  }, [reset]);

  const handleChangePagination = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  // Кнопка «Найти» неактивна, пока не указан хотя бы один диапазон
  const formValues = watch();
  const canSearch = hasAnyDate(formValues.startDate) || hasAnyDate(formValues.endDate);

  const columns: TableColumn<BitrixCallListItem>[] = useMemo(
    () => [
      {
        accessorKey: 'callStartDate',
        header: () => <span>{t('integrations:bitrix.table.callStartDate')}</span>,
        size: 200,
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.callStartDate ? new Date(row.original.callStartDate).toLocaleString() : ''}
          </span>
        ),
      },
      {
        accessorKey: 'callDuration',
        header: () => <span>{t('integrations:bitrix.table.callDuration')}</span>,
        size: 140,
        cell: ({ row }) => <span className="text-sm">{row.original.callDuration ?? ''}</span>,
      },
      {
        accessorKey: 'phoneNumber',
        header: () => <span>{t('integrations:bitrix.table.phoneNumber')}</span>,
        size: 180,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.phoneNumber ?? ''}</span>,
      },
      {
        accessorKey: 'callType',
        header: () => <span>{t('integrations:bitrix.table.callType')}</span>,
        size: 120,
        cell: ({ row }) => <span className="text-sm">{row.original.callType ?? ''}</span>,
      },
      {
        accessorKey: 'isValid',
        header: () => <span>{t('integrations:bitrix.table.isValid')}</span>,
        size: 120,
        cell: ({ row }) => (
          <Badge variant={row.original.isValid ? 'primary' : 'secondary'}>
            {row.original.isValid ? t('integrations:bitrix.table.yes') : t('integrations:bitrix.table.no')}
          </Badge>
        ),
      },
    ],
    [t],
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t('integrations:bitrix.title')}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(handleSearch)}>
        <CardContent className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Datepicker
              mode="range"
              control={control}
              name="startDate"
              label={t('integrations:bitrix.filter.startDate')}
              showTimeInputs
            />
            <Datepicker
              mode="range"
              control={control}
              name="endDate"
              label={t('integrations:bitrix.filter.endDate')}
              showTimeInputs
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          <Button type="submit" disabled={!canSearch}>
            {t('integrations:bitrix.filter.search')}
          </Button>
          <Button type="button" variant="secondary" onClick={handleClear}>
            {t('integrations:bitrix.filter.clear')}
          </Button>
        </CardFooter>
      </form>

      <CardContent>
        {!activeParams ? (
          <div className="text-muted-foreground text-sm p-2">{t('integrations:bitrix.table.needFilters')}</div>
        ) : (
          <AwaitContainer isLoading={isLoading || isFetching}>
            {items.length === 0 ? (
              <div className="text-muted-foreground p-2">{t('integrations:bitrix.table.empty')}</div>
            ) : (
              <Table
                data={items}
                columns={columns}
                getKey={(row) => row.id}
                pagination={{ total, page, perPage: PER_PAGE }}
                type="paginated"
                onChangePagination={handleChangePagination}
                styles={{ size: 'sm' }}
              />
            )}
          </AwaitContainer>
        )}
      </CardContent>
    </Card>
  );
};
